import React from "react";
import type { OperatorPipelineCard, OperatorRecapEntry, OperatorSiteSnapshot, SeoAuditRunId, SiteId } from "@siteagain/shared-types";

import type { Locale } from "../i18n";
import type { OperatorCopy } from "./copy";
import { relativeTime } from "./format";
import { AlertIcon, ArrowIcon, CheckIcon, HourglassIcon, ReportIcon, SearchIcon } from "./icons";
import type { DecisionAction, RecommendationAction } from "./PipelineBoard";
import { SiteMark } from "./SiteMark";

/** The cards that stopped on an error. A declined preview is a decision, not a problem, and is not here. */
export function failedCards(cards: readonly OperatorPipelineCard[]): OperatorPipelineCard[] {
  return cards.filter((card) => card.failed && card.changeSetStatus === "failed");
}

/** The newest completed audit per site in the journal: each one is a report somebody should read. */
export function freshAudits(journal: readonly OperatorRecapEntry[]): Array<OperatorRecapEntry & { auditRunId: SeoAuditRunId }> {
  const seen = new Set<SiteId>();
  const result: Array<OperatorRecapEntry & { auditRunId: SeoAuditRunId }> = [];
  for (const entry of journal) {
    if (entry.kind !== "audit_completed" || entry.auditRunId === null || seen.has(entry.siteId)) continue;
    seen.add(entry.siteId);
    result.push({ ...entry, auditRunId: entry.auditRunId });
  }
  return result;
}

/**
 * Everything on Today that is waiting for a person, in one list, most urgent first: what
 * stopped, what is ready to approve, audits to read, reports to review. Each row carries the
 * one action that moves it on; the rest of the story is a click away.
 */
export function NeedsYou({
  stopped,
  toApprove,
  audits,
  sites,
  reportsToReview,
  copy,
  locale,
  now,
  busyKey,
  onOpenSite,
  onOpenAudit,
  onOpenReports,
  onRecommendation,
  onDecide,
}: {
  stopped: OperatorPipelineCard[];
  toApprove: OperatorPipelineCard[];
  audits: Array<OperatorRecapEntry & { auditRunId: SeoAuditRunId }>;
  sites: OperatorSiteSnapshot[];
  reportsToReview: number;
  copy: OperatorCopy;
  locale: Locale;
  now: number;
  busyKey: string;
  onOpenSite(siteId: SiteId): void;
  onOpenAudit(auditId: SeoAuditRunId): void;
  onOpenReports(): void;
  onRecommendation(card: OperatorPipelineCard, action: RecommendationAction): void;
  onDecide(card: OperatorPipelineCard, action: DecisionAction): void;
}) {
  const t = copy.today;
  const total = stopped.length + toApprove.length + audits.length + (reportsToReview > 0 ? 1 : 0);
  const pending = (siteId: SiteId) => sites.find((site) => site.id === siteId)?.pendingRecommendations ?? 0;

  return (
    <section id="mc-attention" className="mc-inbox" tabIndex={-1} aria-labelledby="mc-inbox-title">
      <header>
        <h2 id="mc-inbox-title">{t.queueTitle}</h2>
        {total > 0 && <span className="mc-count">{total}</span>}
      </header>
      {total === 0 ? (
        <p className="mc-inbox-clear"><CheckIcon />{t.queueEmpty}</p>
      ) : (
        <ul>
          {stopped.map((card) => {
            const busy = busyKey.startsWith(`${card.key}:`);
            return (
              <Row key={card.key} tone="error" icon={<AlertIcon />} kind={t.kind.stopped} card={card} when={relativeTime(card.occurredAt, locale, now)} onOpenSite={onOpenSite}
                detail={<span className="mc-row-error" title={card.errorMessage ?? undefined}>{card.errorMessage ?? copy.pipeline.noErrorMessage}</span>}>
                {card.recommendationId ? (
                  <button type="button" className="primary" disabled={busy} onClick={() => onRecommendation(card, "preview")}>
                    {busyKey === `${card.key}:preview` ? copy.action.working : copy.action.retry}
                  </button>
                ) : <span className="mc-no-retry">{copy.attention.manualHint}</span>}
                <button type="button" onClick={() => onOpenSite(card.siteId)}>{copy.action.details}</button>
              </Row>
            );
          })}
          {toApprove.map((card) => {
            const busy = busyKey.startsWith(`${card.key}:`);
            return (
              <Row key={card.key} tone="wait" icon={<HourglassIcon />} kind={t.kind.approve} card={card} when={relativeTime(card.occurredAt, locale, now)} onOpenSite={onOpenSite}
                detail={card.batch?.eligible ? <span className="mc-checks"><CheckIcon />{copy.pipeline.allChecksPassed}</span> : null}>
                {card.previewUrl && <a className="mc-link" href={card.previewUrl} target="_blank" rel="noreferrer">{copy.action.openPreview}</a>}
                <button type="button" disabled={busy} onClick={() => onDecide(card, "reject")}>{copy.action.reject}</button>
                <button type="button" className="primary" disabled={busy} onClick={() => onDecide(card, "approve")}>
                  {busyKey === `${card.key}:approve` ? copy.action.working : copy.action.approve}
                </button>
              </Row>
            );
          })}
          {audits.map((entry) => (
            <li key={entry.auditRunId} className="mc-inbox-row tone-neutral">
              <span className="mc-inbox-icon" aria-hidden="true"><SearchIcon /></span>
              <div className="mc-inbox-main">
                <small>{t.kind.audit} · {relativeTime(entry.at, locale, now)}</small>
                <div className="mc-inbox-line">
                  <button type="button" className="mc-site" onClick={() => onOpenSite(entry.siteId)}>
                    <SiteMark siteId={entry.siteId} name={entry.siteName} />{entry.siteName}
                  </button>
                  {pending(entry.siteId) > 0 && <span className="mc-muted">{t.auditReady(pending(entry.siteId))}</span>}
                </div>
              </div>
              <div className="mc-actions">
                <button type="button" className="primary" onClick={() => onOpenAudit(entry.auditRunId)}>{copy.audit.open}<ArrowIcon /></button>
              </div>
            </li>
          ))}
          {reportsToReview > 0 && (
            <li className="mc-inbox-row tone-wait">
              <span className="mc-inbox-icon" aria-hidden="true"><ReportIcon /></span>
              <div className="mc-inbox-main">
                <small>{t.kind.report}</small>
                <div className="mc-inbox-line"><strong>{copy.nav.badge.reports(reportsToReview)}</strong></div>
              </div>
              <div className="mc-actions">
                <button type="button" className="primary" onClick={onOpenReports}>{copy.reports.review}<ArrowIcon /></button>
              </div>
            </li>
          )}
        </ul>
      )}
    </section>
  );
}

function Row({
  tone,
  icon,
  kind,
  card,
  when,
  detail,
  onOpenSite,
  children,
}: {
  tone: "error" | "wait";
  icon: React.ReactNode;
  kind: string;
  card: OperatorPipelineCard;
  when: string;
  detail: React.ReactNode;
  onOpenSite(siteId: SiteId): void;
  children: React.ReactNode;
}) {
  return (
    <li className={`mc-inbox-row tone-${tone}`}>
      <span className="mc-inbox-icon" aria-hidden="true">{icon}</span>
      <div className="mc-inbox-main">
        <small>{kind} · {when}</small>
        <div className="mc-inbox-line">
          <button type="button" className="mc-site" onClick={() => onOpenSite(card.siteId)}>
            <SiteMark siteId={card.siteId} name={card.siteName} />{card.siteName}
          </button>
          <strong title={card.title}>{card.title}</strong>
        </div>
        {detail}
      </div>
      <div className="mc-actions">{children}</div>
    </li>
  );
}
